import { FC } from 'react';
import { Link } from 'gatsby';
import { useQueryClient } from 'react-query';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { ButtonOutlinePrimary, Icon, useDrawer } from '@lambdacurry/component-library';
import { MedusaProductVariant, MeQuery, OrdersQuery, SubscriptionStatus } from 'lib/home/generated/types/api';
import { WeekYear } from 'lib/home/util/week-year';
import { selectMealLineItems, selectExtraLineItems } from 'lib/home/util/products';
import { dishContainsAllergens } from 'lib/home/meals/helpers';
import { useExtraProductsQuery, useMyPaymentMethodsQuery, useUpdateOrderMutation } from 'lib/home/generated/queries';
import { Grid, MoreInfoTextButton } from 'lib/components';
import { formatDate } from 'lib/util/date';
import { ExtraCard, Loading, MealCard } from '../..';
import {
  selectDefaultFromPaymentMethods,
  selectExtraProductsByPlanSize,
  updateVariantsInMedusaCart
} from '../../../util/helpers';
import { CurrentOrderSkipped } from './CurrentOrderSkipped';
import { BoxPriceBreakdownDrawer } from '../../Drawers';
import './current-order.scss';
import { CurrentOrderPaused } from './CurrentOrderPaused';

interface CurrentOrderProps {
  className?: string;
  me: MeQuery['me'];
  orders: OrdersQuery;
}

export const CurrentOrder: FC<CurrentOrderProps> = ({ className, me, orders }) => {
  const {
    drawerActions: { toggleDrawer }
  } = useDrawer();

  const queryClient = useQueryClient();
  const { data: paymentMethods } = useMyPaymentMethodsQuery();
  const { data: extras } = useExtraProductsQuery();
  const { mutate: mutateOrder } = useUpdateOrderMutation({
    onSuccess: ({ updateSubscriptionOrder }) => {
      queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
        query.me.subscription.current_order = {
          ...query.me.subscription.current_order,
          ...updateSubscriptionOrder
        };
        return query;
      });
    }
  });

  if (!orders || !extras || !paymentMethods) return <Loading />;

  const { subscription } = orders.me;
  const { current_order, frequency } = subscription;
  const { week, year } = current_order;
  const { medusa_cart } = current_order;

  if (!subscription) return <Loading />;

  const mealLineItems = selectMealLineItems(medusa_cart?.items || []);
  const extraLineItems = selectExtraLineItems(medusa_cart?.items || []);
  const allExtraItems = selectExtraProductsByPlanSize(extras, me);
  const defaultPaymentMethod = selectDefaultFromPaymentMethods(paymentMethods);
  const shippingDate = formatDate(WeekYear.shippingDate({ week, year }));
  const swapDate = formatDate(DateTime.fromISO(current_order.complete_order_at));
  const isWeekSkipped = current_order.skip;
  const isSubscriptionPaused = subscription.status === SubscriptionStatus.Paused;

  const handleUnskipCurrentOrder = () =>
    mutateOrder({ updateSubscriptionOrderInput: { id: current_order.id, skip: false } });

  const updateOrdersQueryDataWithUpdatedMeal = (updatedMeal: MedusaProductVariant) =>
    queryClient.setQueryData(['Orders'], (query: OrdersQuery) => {
      query.me.subscription.current_order.medusa_cart = updateVariantsInMedusaCart(
        query.me.subscription.current_order.medusa_cart,
        updatedMeal
      );

      return query;
    });

  return (
    <>
      <div
        className={classNames('current-order', {
          skipped: isWeekSkipped,
          paused: isSubscriptionPaused
        })}
      >
        <div className="flex flex-wrap items-center justify-between sm:h-12 mb-6">
          <h2 className="sm:m-0">Your Next Box</h2>

          {!isWeekSkipped && !isSubscriptionPaused && (
            <ButtonOutlinePrimary
              className="w-full sm:w-auto"
              onClick={() => mutateOrder({ updateSubscriptionOrderInput: { id: current_order.id, skip: true } })}
            >
              Skip this week
            </ButtonOutlinePrimary>
          )}
        </div>

        <div className="flex item-center">
          {isWeekSkipped ? (
            <div className="flex items-center">
              <Icon aria-hidden="true" name="swap" className="text-primary mr-2 min-w-[24px]" />
              <p className="m-0 flex-auto text-primary text-sm">
                Skipped. You can unskip until <span className="font-medium inline">{swapDate}</span>
              </p>
            </div>
          ) : (
            <div className="flex flex-col lg:flex-row flex-1 gap-y-2 lg:items-center">
              <div className="flex items-center">
                <Icon aria-hidden="true" name="swap" className="text-primary mr-2" />
                <p className="m-0 text-xs sm:text-sm">
                  Swap until <span className="font-medium inline text-primary text-xs sm:text-sm">{swapDate}</span>
                </p>
              </div>

              <div className="flex items-center lg:ml-4">
                <p className="m-0 text-xs sm:text-sm">
                  <Icon aria-hidden="true" name="truck" className="text-primary mr-2" />
                  Shipping <span className="font-medium inline text-primary text-xs sm:text-sm">{shippingDate}</span>
                </p>
              </div>

              <div className="flex gap-4 lg:gap-2 items-center h-[21px] lg:ml-auto">
                <MoreInfoTextButton
                  data-cy="box-price-breakdown-toggle"
                  onClick={() =>
                    toggleDrawer({
                      name: 'box-price-breakdown',
                      data: { currentOrder: current_order, defaultPaymentMethod, frequency }
                    })
                  }
                >
                  Price Breakdown
                </MoreInfoTextButton>
              </div>
            </div>
          )}
        </div>

        <div className="current-order-meals-container">
          {isWeekSkipped && <CurrentOrderSkipped frequency={frequency} unskip={handleUnskipCurrentOrder} />}
          {isSubscriptionPaused && <CurrentOrderPaused reason={subscription.status_reason} />}

          <Grid className={classNames(`current-order-meals auto-rows-fr my-6`, className)}>
            {mealLineItems.map((item, index) => (
              <MealCard
                actions={
                  <ButtonOutlinePrimary
                    as={buttonProps => <Link {...buttonProps} to={'/swap-meal'} state={{ item }} />}
                    data-cy={`swap-button-${index}`}
                    icon={<Icon name="swap" />}
                  >
                    Swap Meal
                  </ButtonOutlinePrimary>
                }
                onProductVariantUpdate={updateOrdersQueryDataWithUpdatedMeal}
                className="col-span-12 sm:col-span-6 md:col-span-4"
                id={`meal-card-${item.variant.id}`}
                key={item.id}
                meal={item.variant}
              />
            ))}
          </Grid>
        </div>
      </div>

      {!isWeekSkipped && !isSubscriptionPaused && (
        <>
          {extraLineItems.length > 0 && (
            <div className="my-12 current-order-extras">
              <h3 className="mb-4">Your Extras</h3>
              <Grid className="auto-rows-fr">
                {extraLineItems.map((extra, index) => {
                  return (
                    <ExtraCard
                      className="select-extra-card col-span-12 sm:col-span-6 lg:col-span-4"
                      extra={extra.variant}
                      quantity={extra.quantity}
                      price={extra.unit_price}
                      key={extra.id}
                      index={index}
                    />
                  );
                })}
              </Grid>
            </div>
          )}

          {allExtraItems?.length > 0 && !!extraLineItems && (
            <div className="my-12 current-order-popular-extras">
              <h3 className="mb-4">Popular Extras</h3>
              <Grid className="auto-rows-fr">
                {allExtraItems
                  .filter(extraOption => !extraLineItems.find(x => x.variant_id === extraOption.id))
                  .map((extraSelection, index) => {
                    const { meal_preferences } = me;
                    const { dietary_information } = extraSelection;
                    const alert = dishContainsAllergens(dietary_information, meal_preferences.allergens);
                    return (
                      <ExtraCard
                        className="select-extra-card col-span-12 sm:col-span-6 lg:col-span-4"
                        extra={extraSelection}
                        price={extraSelection.prices[0].amount}
                        key={extraSelection.id}
                        alert={alert}
                        index={index}
                      />
                    );
                  })}
              </Grid>
            </div>
          )}
        </>
      )}

      <BoxPriceBreakdownDrawer />
    </>
  );
};
